// material
import { alpha, styled } from "@mui/material/styles";
import {
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  Paper,
  Box,
  Divider,
  CircularProgress,
} from "@mui/material";
// utils
import { fShortenNumber } from "../../../utils/formatNumber";
import { BaseChart } from "src/components/charts/BaseOptionChart";
import { useEffect, useMemo } from "react";

// ----------------------------------------------------------------------

const RootStyle = styled(Card)(({ theme }) => ({
  boxShadow: theme.customShadows.z4,
  textAlign: "center",
  padding: theme.spacing(2, 1.5),
  color: theme.palette.text.primary,
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.grey[200]}`,
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2.5, 2),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3, 2.5),
  },
}));

const RootStyleNew = styled(Card)(
  ({ theme, maxwidth, minheight = "100%", backgroundcolor, color }) => ({
    maxWidth: maxwidth,
    minHeight: minheight,
    boxShadow: theme.customShadows.z4,
    backgroundColor: backgroundcolor,
    color: color,
    padding: theme.spacing(2),
    display: "grid",
    gap: "6px 0",
    borderRadius: theme.shape.borderRadiusMd,
    border: `1px solid ${theme.palette.grey[200]}`,
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2.5),
      gap: "8px 0",
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
  })
);

export const StyledFormControl = styled(FormControl)(
  ({ theme, bgcolor, maxwidth = "100px" }) => {
    return {
      background: `linear-gradient(35deg, ${theme.lighter} 10%, ${theme.light} 60%, ${theme.main} 88%, ${bgcolor} 100%)`,
      borderRadius: "5px",
      maxWidth: maxwidth,
    };
  }
);

const StyledPaper = styled(Paper)(({ theme }) => ({
  height: "auto",
  minHeight: { xs: "300px", sm: "350px", md: "400px" },
  minWidth: { xs: "280px", sm: "320px", md: "350px" },
  maxWidth: { xs: "100%", sm: "380px", md: "400px" },
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.grey[200]}`,
  background: theme.palette.background.paper,
  overflow: 'visible',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2.5),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3),
  },
}));

const IconWrapperStyle = styled("div")(({ theme, ...props }) => ({
  margin: "auto",
  display: "flex",
  borderRadius: "50%",
  alignItems: "center",
  width: { xs: theme.spacing(6), sm: theme.spacing(7), md: theme.spacing(8) },
  height: { xs: theme.spacing(6), sm: theme.spacing(7), md: theme.spacing(8) },
  justifyContent: "center",
  marginBottom: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
  color: theme.palette.primary.main,
  background: alpha(theme.palette.primary.main, 0.08),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
}));

const IconWrapper = styled(Box)(({ svgcolor, ...props }) => ({
  margin: "2px",
  border: "none",
}));
// ----------------------------------------------------------------------

export default function DashboardCard({
  title,
  count,
  icon,
  colorTheme,
  unit = "",
}) {
  return (
    <RootStyle
      sx={{ backgroundColor: colorTheme?.lighter, color: colorTheme?.darker }}
    >
      <IconWrapperStyle
        sx={{
          color: colorTheme?.dark,
          backgroundImage: `${
            colorTheme
              ? `linear-gradient(135deg, ${alpha(
                  colorTheme?.dark,
                  0
                )} 0%, ${alpha(colorTheme?.dark, 0.24)} 100%)`
              : undefined
          }`,
        }}
      >
        {icon}
      </IconWrapperStyle>
      <Typography 
        variant="subtitle2" 
        sx={{ 
          opacity: 0.72,
          fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' }
        }}
      >
        {title}
      </Typography>
      <Typography 
        variant="h3" 
        sx={{ 
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
          fontWeight: { xs: 600, sm: 700, md: 700 }
        }}
      >
        {count === "N/A" ? "N/A" : fShortenNumber(count)}
        {unit ? " " + unit : ""}
      </Typography>
    </RootStyle>
  );
}

export const DashboardNumberStatsCard = ({
  title,
  count,
  icon,
  footer,
  percentChange,
  colorTheme,
  bgColor,
  fontColor,
  dropdownValues,
  isFetching,
}) => {
  return (
    <RootStyleNew
      backgroundcolor={bgColor}
      color={fontColor}
      // maxwidth="300px"
      // minheight="142px"
    >
      <Stack
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700 }}
        >
          {title}
        </Typography>
      </Stack>
      {isFetching ? (
        <CircularProgress />
      ) : (
        <>
          <Typography variant="h3">{count}</Typography>
          <Stack flexDirection="row" alignItems="center">
            {icon && (
              <IconWrapper>
                <img src={icon} alt={`${title}-icon`} />
              </IconWrapper>
            )}

            <Typography variant="caption" sx={{ marginLeft: 0.5 }}>
              {percentChange ?? footer}
            </Typography>
          </Stack>
        </>
      )}
    </RootStyleNew>
  );
};

export const DashboardDonutStatsCard = ({
  stats,
  success,
  error,
  isFetchingChargerList,
}) => {
  const dataSeries = useMemo(() => {
    if (stats.length > 0) {
      const series = stats.reduce(
        (dataSeries, { title, count }) =>
          title !== "Total Devices" ? [...dataSeries, count] : dataSeries,
        []
      );
      return series;
    }
    return [];
  }, [stats]);

  const options = useMemo(
    () => ({
      chart: {
        type: "donut",
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        toolbar: {
          show: false
        },
        animations: { enabled: false }
      },
      legend: {
        show: false
      },
      plotOptions: {
        pie: {
          donut: {
            size: "70%",
            labels: {
              show: false,
              name: {
                show: false
              },
              value: {
                show: false
              }
            }
          },
          expandOnClick: false,
        },
      },
      colors: [success, error],
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['#fff']
      },
      states: {
        hover: {
          filter: {
            type: 'lighten',
            value: 0.1
          }
        }
      }
    }),
    [error, success]
  );
  return (
    <StyledPaper elevation={4}>
      {stats &&
        stats.length > 0 &&
        stats.map(
          ({ title, count, icon, svgColor, fontColor, isFetching }, index) => {
            return (
              <Stack key={`${title}-${index}-${count}`}>
                <Box paddingX={1} marginY={1}>
                  <Stack spacing={1}>
                    <Stack
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bolder" }}
                      >
                        {title}
                      </Typography>
                      <IconWrapper>
                        <img src={icon} alt={`${title}-icon`} />
                      </IconWrapper>
                    </Stack>
                    <Typography variant="h3" sx={{ color: fontColor }}>
                      {isFetching ? <CircularProgress /> : count}
                    </Typography>
                  </Stack>
                </Box>
                {index < stats.length - 1 && <Divider variant="middle" />}
              </Stack>
            );
          }
        )}
      {isFetchingChargerList ? (
        <CircularProgress />
      ) : (
        <Stack spacing={2} alignItems="center">
          <Stack direction="row" spacing={4} alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: success,
                }}
              />
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                Online Devices
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: error,
                }}
              />
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                Offline Devices
              </Typography>
            </Stack>
          </Stack>
          <Box sx={{ 
            width: '100%', 
            height: { xs: 200, sm: 225, md: 250 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <BaseChart
              series={dataSeries}
              options={options}
              type="donut"
              height="100%"
              width="100%"
            />
          </Box>
        </Stack>
      )}
    </StyledPaper>
  );
};

export const DashboardBarGraphStatsCard = ({
  title,
  colorTheme,
  bgColor,
  fontColor,
  xaxis,
  xaxisType,
  yaxis,
}) => {
  const isCumulativeUsage = title === "Cumulative Usage";

  const options = useMemo(
    () => ({
      chart: {
        type: "bar",
        height: { xs: 250, sm: 300, md: 350 },
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        toolbar: {
          show: false,
        },
        background: 'transparent',
        animations: { enabled: false }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "60%",
          endingShape: "rounded",
          borderRadius: 4,
          dataLabels: {
            position: 'top'
          }
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 0,
        colors: ["transparent"],
      },
      xaxis: {
        categories: xaxis,
        type: xaxisType,
        title: {
          text: "Time Period",
          style: {
            fontSize: '12px',
            fontWeight: 500,
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            color: '#718096'
          },
        },
        labels: {
          style: {
            fontSize: '11px',
            fontWeight: 400,
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            colors: '#4A5568'
          }
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      },
      yaxis: {
        title: {
          text: "Energy (kWh)",
          style: {
            fontSize: '12px',
            fontWeight: 500,
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            color: '#718096'
          },
        },
        labels: {
          style: {
            fontSize: '11px',
            fontWeight: 400,
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            colors: '#4A5568'
          },
          formatter: (value) => { return (value / 1000).toFixed(1) }
        }
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.5,
          gradientToColors: [colorTheme.light],
          inverseColors: false,
          opacityFrom: 0.8,
          opacityTo: 0.6,
        }
      },
      colors: [colorTheme.main],
      grid: {
        show: true,
        borderColor: '#E2E8F0',
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        }
      },
      tooltip: {
        style: {
          fontSize: '12px',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        },
        y: {
          formatter: (value) => { return `${(value / 1000).toFixed(2)} kWh` }
        }
      }
    }),
    [colorTheme.dark, colorTheme.main, colorTheme.light, xaxis, xaxisType]
  );

  const series = useMemo(
    () => [
      {
        name: "Usage",
        data: yaxis,
      },
    ],
    [yaxis]
  );
  return (
    <RootStyleNew
      backgroundcolor={bgColor}
      color={fontColor}
      // maxwidth="473px"
      // minheight="120px"
    >
      <Stack
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography
          variant="subtitle1"
          sx={{ 
            fontWeight: 700,
            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
          }}
        >
          {title}
        </Typography>
      </Stack>
      <Box sx={{ 
        width: '100%', 
        height: { xs: 250, sm: 300, md: 400 },
        overflow: 'hidden'
      }}>
        <BaseChart
          options={options}
          series={series}
          height="100%"
          width="100%"
          type="bar"
        />
      </Box>
    </RootStyleNew>
  );
};

export const DashboardMapCard = ({ title, bgColor, fontColor, children }) => {
  return (
    <RootStyleNew backgroundcolor={bgColor} color={fontColor}>
      <Stack>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            marginBottom: "5px",
            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
          }}
        >
          {title}
        </Typography>
        {children}
      </Stack>
    </RootStyleNew>
  );
};
